import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type { IInvoice, IInvoiceItem } from "../../types/Invoice";

interface InvoiceLinesProps {
  invoice: IInvoice;
}

const discountPercent = (item: IInvoiceItem): number =>
  item.discountInPercent ?? item.discount ?? 0;

const lineAmountExcludingVat = (item: IInvoiceItem): number => {
  const baseAmount = Number(item.quantity || 0) * Number(item.price || 0);
  return baseAmount - (baseAmount * discountPercent(item)) / 100;
};

export const InvoiceLines: FC<InvoiceLinesProps> = ({ invoice }) => {
  const { t, i18n } = useTranslation(["common", "invoice"]);
  const locale =
    i18n.language.startsWith("no") || i18n.language.startsWith("nb")
      ? "nb-NO"
      : "en-GB";
  const formatAmount = (value: number) =>
    new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  const subtotal =
    invoice.amounts?.totalAmountWithoutVat ??
    invoice.items.reduce((sum, item) => sum + lineAmountExcludingVat(item), 0);
  const vat =
    invoice.amounts?.totalVatAmount ??
    invoice.items.reduce(
      (sum, item) =>
        sum + (lineAmountExcludingVat(item) * Number(item.vat || 0)) / 100,
      0,
    );
  const total = invoice.amounts?.totalAmount ?? subtotal + vat;

  if (invoice.items.length === 0) {
    return (
      <p className="px-4 py-8 text-sm text-muted">
        {t("invoice:actions.no_items")}
      </p>
    );
  }

  return (
    <div className="py-4">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="min-w-[42rem] w-full text-left text-sm">
          <thead className="bg-surface-tableHead text-xs uppercase tracking-wide text-muted">
            <tr>
              <th className="px-4 py-3 font-medium">
                {t("invoice:pdf.description")}
              </th>
              <th className="px-4 py-3 text-right font-medium">
                {t("invoice:pdf.quantity")}
              </th>
              <th className="px-4 py-3 text-right font-medium">
                {t("invoice:details.unit_price")}
              </th>
              <th className="px-4 py-3 text-right font-medium">
                {t("common:vat")}
              </th>
              <th className="px-4 py-3 text-right font-medium">
                {t("invoice:details.amount_excluding_vat")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {invoice.items.map((item, index) => {
              const description = item.itemTitle || item.description;
              const secondaryDescription =
                item.itemTitle && item.description !== item.itemTitle
                  ? item.description
                  : undefined;
              const discount = discountPercent(item);

              return (
                <tr key={`${description}-${index}`} className="text-text">
                  <td className="px-4 py-3">
                    <p className="font-medium">{description || "—"}</p>
                    {secondaryDescription && (
                      <p className="mt-0.5 text-xs text-muted">
                        {secondaryDescription}
                      </p>
                    )}
                    {discount > 0 && (
                      <p className="mt-0.5 text-xs text-muted">
                        {t("invoice:details.discount", {
                          percent: formatAmount(discount),
                        })}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {item.displayQuantity ||
                      `${formatAmount(Number(item.quantity || 0))}${
                        item.unit ? ` ${item.unit}` : ""
                      }`}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {formatAmount(Number(item.price || 0))}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {formatAmount(Number(item.vat || 0))} %
                  </td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums">
                    {formatAmount(lineAmountExcludingVat(item))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <dl className="ml-auto mt-4 w-full max-w-xs divide-y divide-border text-sm">
        <div className="flex justify-between gap-4 py-2">
          <dt className="text-muted">{t("invoice:actions.subtotal")}</dt>
          <dd className="tabular-nums text-text">
            {formatAmount(subtotal)} NOK
          </dd>
        </div>
        <div className="flex justify-between gap-4 py-2">
          <dt className="text-muted">{t("invoice:details.vat_total")}</dt>
          <dd className="tabular-nums text-text">{formatAmount(vat)} NOK</dd>
        </div>
        <div className="flex justify-between gap-4 py-3 font-semibold">
          <dt className="text-text">{t("invoice:pdf.total")}</dt>
          <dd className="tabular-nums text-text">{formatAmount(total)} NOK</dd>
        </div>
      </dl>
    </div>
  );
};
